import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/pro-light-svg-icons'

import { useFilters } from '@domain-group/react-hooks'

import { Box, Button, DashboardModule } from '@domain-group/suit-theme'

import DashboardModuleFilters from '@components/DashboardModuleFilters'

import useAdAccountGroupLink from '@hooks/useAdAccountGroupLink'
import useAdAccountGroupLinks from '@hooks/useAdAccountGroupLinks'

import AdAccountGroupLinkListItem from './AdAccountGroupLinkListItem'

const buildPageFilterFields = () => {
  const style = { marginBottom: 16 }

  return [
    {
      component: 'DynamicInput',
      componentProps: {
        inputComponent: 'input',
        style,
      },
      key: 'linkableEntityName',
      label: 'Filter by Name',
    },
  ]
}

interface AdAccountGroupLinksProps {
  adAccountGroupId: number,
  linkableType: string,
}

const AdAccountGroupLinks = (props: AdAccountGroupLinksProps) => {
  const { adAccountGroupId, linkableType } = props

  const filterPayload = useFilters()

  const {
    pageFilters,
  } = filterPayload

  const options = {
    filters: { adAccountGroupId, linkableType, ...pageFilters },
    performHttpRequests: !!adAccountGroupId,
    requestOptions: {
      include: 'LinkableEntity',
    },
  }

  const adAccountGroupLinksPayload = useAdAccountGroupLinks(options)

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAdAccountGroupLinks,
    filteredAdAccountGroupLinksCount,
    hasFilteredAdAccountGroupLinks,
    loading,
  } = adAccountGroupLinksPayload

  const {
    callbacks: {
      launchBulkCreateAdAccountGroupLinksModal,
    },
  } = useAdAccountGroupLink({ adAccountGroupId, linkableType })

  const isShowingClients = linkableType === 'Client'

  const moduleTitle = isShowingClients ? 'Organisations' : 'Users'

  return (
    <DashboardModule
      boxProps={{ borderBottom: loading ? 'none' : '1px solid lineColor' }}
      contentBoxProps={{ flexDirection: 'column' }}
      headerRight={<DashboardModule.FilterHeader {...filterPayload} />}
      loading={loading}
      title={moduleTitle}
    >
      <DashboardModuleFilters
        filterFields={buildPageFilterFields()}
        {...filterPayload}
      />

      <Box flexDirection="column">
        <DashboardModule.ScrollableSection flexDirection="column">
          {!loading && !hasFilteredAdAccountGroupLinks && (
            <DashboardModule.BlankState>
              <DashboardModule.BlankState.Title>
                No {linkableType}s linked
              </DashboardModule.BlankState.Title>

              <DashboardModule.BlankState.Paragraph>
                Please adjust your filters or link a {linkableType}
              </DashboardModule.BlankState.Paragraph>
            </DashboardModule.BlankState>
          )}

          {!loading && filteredAdAccountGroupLinks.map(adAccountGroupLink => (
            <AdAccountGroupLinkListItem
              adAccountGroupLink={adAccountGroupLink}
              key={adAccountGroupLink.id}
            />
          ))}
        </DashboardModule.ScrollableSection>

        <DashboardModule.LoadMoreFooter
          boxProps={{ borderTop: loading ? 'none' : '1px solid' }}
          callbacks={{ loadMore }}
          canLoadMore={canLoadMore}
          entityCount={filteredAdAccountGroupLinksCount}
          loading={loading}
        >
          <Button
            buttonStyle="secondaryEdit"
            icon={<FontAwesomeIcon icon={faPlus} />}
            onClick={() => launchBulkCreateAdAccountGroupLinksModal()}
            size={['medium', 'small']}
            width={['100%', 'fit-content']}
          >
            {isShowingClients ? 'Link Organisations' : 'Link Users'}
          </Button>
        </DashboardModule.LoadMoreFooter>
      </Box>
    </DashboardModule>
  )
}

export default AdAccountGroupLinks
